import { getApiUrl } from '../config/api';

export const DEFAULT_WEIGHTS = {
  budget: 25,
  tripPurpose: 20,
  amenities: 20,
  location: 15,
  groupSize: 10,
  pastBehavior: 5,
  availability: 5,
};

let cachedWeights = { ...DEFAULT_WEIGHTS };
let cachedPersonalization = null;
let personalizationFetchedAt = 0;

const AMENITY_KEYWORDS = {
  wifi: ['wifi', 'wi-fi', 'internet'],
  parking: ['parking', 'car park'],
  pool: ['pool', 'swimming'],
  kitchen: ['kitchen', 'cooking'],
  breakfast: ['breakfast'],
  ac: ['ac', 'air conditioning'],
  pet: ['pet', 'pets'],
  family: ['family', 'kids', 'children'],
};

const PURPOSE_HINTS = {
  family: ['family', 'kids', 'children'],
  couple: ['couple', 'romantic'],
  business: ['business', 'work', 'workcation'],
};

const normalizeWeights = (raw = DEFAULT_WEIGHTS) => {
  const merged = { ...DEFAULT_WEIGHTS, ...raw };
  const total = Object.values(merged).reduce((s, v) => s + Number(v || 0), 0) || 100;
  const factor = 100 / total;
  return Object.fromEntries(Object.entries(merged).map(([k, v]) => [k, Number(v) * factor]));
};

const haystack = (property) =>
  [
    ...(property.amenities || []),
    property.description || '',
    property.type || '',
    ...(property.houseRules || []),
  ]
    .join(' ')
    .toLowerCase();

export const getMatchTier = (score) => {
  if (score >= 90) return 'high';
  if (score >= 70) return 'medium';
  return 'low';
};

const partScores = (property, intent, personalization) => {
  const price = Number(property.pricePerNight) || 0;
  const max = Number(intent.budgetMax);
  let budget = 55;
  if (max && price) {
    if (price <= max) budget = 100;
    else if (price <= max * 1.1) budget = 75;
    else if (price <= max * 1.25) budget = 45;
    else budget = 15;
  }

  const purpose = String(intent.tripPurpose || intent.guestType || '').toLowerCase();
  const text = haystack(property);
  let tripPurpose = 60;
  if (purpose) {
    const hints = PURPOSE_HINTS[purpose] || [purpose];
    const hits = hints.filter((h) => text.includes(h)).length;
    if (purpose === 'family' && (property.bedrooms || 0) >= 2) tripPurpose = 95;
    else if (purpose === 'couple') tripPurpose = 88;
    else if (purpose === 'business' && text.includes('wifi')) tripPurpose = 90;
    else tripPurpose = Math.min(100, 50 + hits * 18);
  }

  const wanted = intent.amenities || [];
  let amenities = 65;
  if (wanted.length) {
    let matched = 0;
    wanted.forEach((tag) => {
      const keys = AMENITY_KEYWORDS[tag] || [tag];
      if (keys.some((k) => text.includes(k))) matched += 1;
    });
    amenities = Math.round((matched / wanted.length) * 100);
  }

  const dest = String(intent.destination || '').toLowerCase();
  let location = 60;
  if (dest) {
    const city = String(property.city || '').toLowerCase();
    if (city.includes(dest) || dest.includes(city)) location = 100;
    else if ((property.distanceFromCenter || 99) <= 3) location = 80;
    else location = 40;
  }

  const guests = Number(intent.guests?.adults || 0) + Number(intent.guests?.children || 0);
  const maxGuests = Number(property.maxGuests) || 2;
  let groupSize = 70;
  if (guests) {
    if (maxGuests >= guests) groupSize = 100;
    else if (maxGuests >= guests - 1) groupSize = 70;
    else groupSize = 30;
  }

  const id = String(property._id || property.id || '');
  const wishlist = new Set(personalization?.wishlistIds || []);
  const booked = new Set(personalization?.bookedPropertyIds || []);
  const cities = new Set((personalization?.favoriteCities || []).map((c) => c.toLowerCase()));
  let pastBehavior = 50;
  if (wishlist.has(id)) pastBehavior += 35;
  if (booked.has(id)) pastBehavior += 20;
  if (cities.has(String(property.city || '').toLowerCase())) pastBehavior += 25;
  pastBehavior = Math.min(100, pastBehavior);

  let availability = 55;
  if (intent.checkIn && intent.checkOut) availability = 90;
  else if (intent.checkIn || intent.checkOut) availability = 70;

  return { budget, tripPurpose, amenities, location, groupSize, pastBehavior, availability };
};

const buildReasons = (property, intent, parts) => {
  const reasons = [];
  if (parts.budget >= 80 && intent.budgetMax) reasons.push('Within your budget.');
  if (parts.tripPurpose >= 80) {
    if (intent.guestType === 'family') reasons.push('Ideal for family travel.');
    else reasons.push('Matches your trip purpose.');
  }
  if (property.bedrooms >= 2) reasons.push(`${property.bedrooms} bedrooms`);
  const text = haystack(property);
  if (text.includes('parking')) reasons.push('Free parking');
  if (text.includes('kitchen')) reasons.push('Kitchen included');
  if (text.includes('wifi')) reasons.push('High-speed Wi-Fi');
  if ((property.distanceFromCenter || 99) <= 2) reasons.push('Near major attractions');
  if (parts.pastBehavior >= 80) reasons.push('Matches your previous preferences');
  if (!reasons.length) reasons.push('Aligns with your search preferences');
  return [...new Set(reasons)].slice(0, 5);
};

const buildHeadline = (intent, score) => {
  if (score >= 90) {
    if (intent.guestType === 'family') return 'Perfect for your family trip';
    if (intent.guestType === 'couple') return 'Perfect for your getaway';
    return 'Excellent match for you';
  }
  if (score >= 75) return 'Strong match for your trip';
  return 'Good option to consider';
};

export const scorePropertyMatch = (property, intent = {}, personalization = {}, weights = cachedWeights) => {
  const w = normalizeWeights(weights);
  const parts = partScores(property, intent, personalization);
  const score = Math.round(
    Object.entries(parts).reduce((sum, [key, val]) => sum + val * (w[key] || 0), 0) / 100
  );
  const clamped = Math.max(0, Math.min(100, score));
  return {
    score: clamped,
    tier: getMatchTier(clamped),
    headline: buildHeadline(intent, clamped),
    reasons: buildReasons(property, intent, parts),
  };
};

export const inferIntentFromSearchParams = (searchParams = {}) => {
  const guests = searchParams.guests || {};
  const adults = Number(guests.adults) || 2;
  const children = Number(guests.children) || 0;
  let guestType = null;
  if (children > 0) guestType = 'family';
  else if (adults === 1) guestType = 'solo';
  else if (adults === 2) guestType = 'couple';
  else if (adults > 2) guestType = 'group';

  return {
    destination: searchParams.location || searchParams.city || '',
    budgetMax: searchParams.maxPrice ?? searchParams.priceRange?.[1] ?? null,
    guests: { adults, children },
    guestType,
    tripPurpose: guestType,
    checkIn: searchParams.checkIn || null,
    checkOut: searchParams.checkOut || null,
    amenities: searchParams.amenities || [],
  };
};

export const getSearchIntent = () => {
  try {
    const raw = localStorage.getItem('searchResults');
    if (!raw) return inferIntentFromSearchParams({});
    const parsed = JSON.parse(raw);
    return inferIntentFromSearchParams({
      ...(parsed.searchParams || {}),
      location: parsed.searchParams?.location || parsed.query?.city || parsed.query?.input,
      maxPrice: parsed.searchParams?.priceRange?.[1],
    });
  } catch {
    return inferIntentFromSearchParams({});
  }
};

export const fetchSmartMatchConfig = async () => {
  try {
    const res = await fetch(getApiUrl('/api/smart-match/config'));
    const json = await res.json();
    if (json?.weights) cachedWeights = json.weights;
  } catch {
    cachedWeights = { ...DEFAULT_WEIGHTS };
  }
  return cachedWeights;
};

export const fetchPersonalization = async (force = false) => {
  const token = localStorage.getItem('token');
  if (!token) {
    cachedPersonalization = { wishlistIds: [], bookedPropertyIds: [], favoriteCities: [] };
    return cachedPersonalization;
  }
  if (!force && cachedPersonalization && Date.now() - personalizationFetchedAt < 5 * 60 * 1000) {
    return cachedPersonalization;
  }
  try {
    const res = await fetch(getApiUrl('/api/smart-match/personalization'), {
      headers: { Authorization: `Bearer ${token}` },
    });
    const json = await res.json();
    if (json.success) {
      cachedPersonalization = json;
      personalizationFetchedAt = Date.now();
    }
  } catch {
    cachedPersonalization = { wishlistIds: [], bookedPropertyIds: [], favoriteCities: [] };
  }
  return cachedPersonalization || { wishlistIds: [], bookedPropertyIds: [], favoriteCities: [] };
};

export const scoreProperties = (properties, intent, personalization) =>
  properties.map((property) => ({
    ...property,
    smartMatch: scorePropertyMatch(property, intent, personalization),
  }));
